import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { EASE, Reveal } from '../components/motion.tsx';
import { Avatar, Button, Callout, Chip, Dialog, Empty, Field, PageHead, Panel, Segmented, Skeleton, Switch, Tabs, useConfirm, useToast } from '../components/ui.tsx';
import { api, ApiError, get, patch, post, put, qs } from '../lib/api.ts';
import { relative } from '../lib/format.ts';
import { useLookups, useMe } from '../lib/session.ts';

interface UserRow {
  id: string;
  email: string;
  name: string;
  employeeNo: string | null;
  employeeStatus: string | null;
  status: 'active' | 'blocked' | 'pending';
  superadmin: boolean;
  locked: boolean;
  lastLoginAt: string | null;
  apps: { app: string; roles: number }[];
  offices: number;
}
interface UserDetail {
  id: string;
  email: string;
  name: string;
  displayName: string | null;
  employeeNo: string | null;
  status: 'active' | 'blocked' | 'pending';
  superadmin: boolean;
  mfa: boolean;
  locked: boolean;
  lastLoginAt: string | null;
  activeSessions: number;
  roles: { id: string; roleId: string; app: string; key: string; name: string; officeId: string | null; viaJobPosition: boolean }[];
  permissions: { id: string; app: string; key: string; name: string }[];
  offices: { id: string; name: string }[];
}
interface Catalogue {
  apps: { code: string; name: string }[];
  roles: { id: string; appCode: string; key: string; name: string; description: string | null; permissionIds: string[]; users: number }[];
  permissions: { id: string; appCode: string; key: string; name: string; description: string | null }[];
  canGrant: string[];
}

const err = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong.');

/** Client-side mirror of the API's wildcard rule, used only to grey out what you cannot grant. */
function holds(granted: string[], key: string) {
  if (granted.includes('*') || granted.includes(key)) return true;
  const parts = key.replace(/\.\*$/, '').split('.');
  for (let i = parts.length; i > 0; i--) if (granted.includes(`${parts.slice(0, i).join('.')}.*`)) return true;
  return false;
}

export function Access() {
  const [tab, setTab] = useState<'people' | 'roles'>('people');
  const cat = useQuery({ queryKey: ['access-catalogue'], queryFn: () => get<Catalogue>('/api/access/catalogue') });
  return (
    <div>
      <PageHead
        eyebrow={`Access · ${cat.data?.apps.length ?? '…'} apps · ${cat.data?.roles.length ?? '…'} roles`}
        title={
          <>
            Who can open <em>which door.</em>
          </>
        }
        lede="One place for sign-in and permissions across every GX app. You can only hand out access you hold yourself, and every change reaches the legacy ERP tables the other apps still read."
      />
      <Tabs idPrefix="access" value={tab} onChange={setTab} tabs={[{ id: 'people', label: 'People' }, { id: 'roles', label: 'Roles', count: cat.data?.roles.length }]} />
      <div style={{ marginTop: 28 }}>{tab === 'people' ? <People cat={cat.data} /> : <Roles cat={cat.data} />}</div>
    </div>
  );
}

// ---------------------------------------------------------------- People

function People({ cat }: { cat?: Catalogue }) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'blocked' | 'superadmin'>('all');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string | null>(null);
  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(q);
      setPage(1);
    }, 220);
    return () => clearTimeout(t);
  }, [q]);
  const list = useQuery({ queryKey: ['access-users', debounced, status, page], queryFn: () => get<{ total: number; page: number; size: number; items: UserRow[] }>(`/api/access/users${qs({ q: debounced, status: status === 'all' ? undefined : status, page })}`), placeholderData: (p) => p });
  const pages = list.data ? Math.max(1, Math.ceil(list.data.total / list.data.size)) : 1;
  const active = selected ?? list.data?.items[0]?.id ?? null;

  return (
    <Reveal className="grid-12">
      <Reveal.Item className="col-5 stack" style={{ alignContent: 'start' }}>
        <div className="stack" style={{ ['--gap' as string]: '10px' }}>
          <div className="search-field">
            <Icon name="search" width={16} height={16} />
            <input className="input" placeholder="Name, e-mail or employee no." value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search people" />
          </div>
          <Segmented label="Filter" idPrefix="access-filter" value={status} onChange={(v) => { setStatus(v); setPage(1); }} options={[{ id: 'all', label: 'All' }, { id: 'active', label: 'Active' }, { id: 'blocked', label: 'Blocked' }, { id: 'superadmin', label: 'Superadmin' }]} />
        </div>
        <div className="panel" style={{ padding: 6 }}>
          {list.isLoading ? (
            <Skeleton h={320} />
          ) : !list.data?.items.length ? (
            <div style={{ padding: 20 }}><p className="muted">No one matches.</p></div>
          ) : (
            <ul className="list-plain" role="listbox" aria-label="People">
              {list.data.items.map((u, i) => (
                <motion.li key={u.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: EASE, delay: Math.min(i, 12) * 0.02 }}>
                  <button type="button" className="access-row" aria-selected={u.id === active} onClick={() => setSelected(u.id)}>
                    <Avatar name={u.name} size={32} />
                    <span className="access-row__main">
                      <span className="access-row__name">{u.name}</span>
                      <span className="access-row__meta">{u.employeeNo ? `${u.employeeNo} · ` : ''}{u.email}</span>
                    </span>
                    <span className="access-row__side">
                      {u.superadmin ? <Chip tone="accent">Superadmin</Chip> : u.status === 'blocked' ? <Chip tone="bad">Blocked</Chip> : u.locked ? <Chip tone="warn">Locked</Chip> : <span className="faint mono" style={{ fontSize: 10.5 }}>{u.apps.reduce((n, a) => n + a.roles, 0)} roles</span>}
                    </span>
                  </button>
                </motion.li>
              ))}
            </ul>
          )}
        </div>
        {pages > 1 ? (
          <div className="row row--between">
            <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <span className="faint mono" style={{ fontSize: 11 }}>{page} / {pages} · {list.data?.total}</span>
            <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        ) : null}
      </Reveal.Item>
      <Reveal.Item className="col-7">
        <AnimatePresence mode="wait">
          {active && cat ? (
            <motion.div key={active} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} transition={{ duration: 0.4, ease: EASE }}>
              <UserPanel id={active} cat={cat} />
            </motion.div>
          ) : (
            <Empty title="Pick someone on the left." />
          )}
        </AnimatePresence>
      </Reveal.Item>
    </Reveal>
  );
}

function useAccessMutation<T>(fn: (v: T) => Promise<unknown>, success?: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['access-user'] });
      void qc.invalidateQueries({ queryKey: ['access-users'] });
      void qc.invalidateQueries({ queryKey: ['access-catalogue'] });
      if (success) toast({ title: success });
    },
    onError: (e) => toast({ title: err(e), tone: 'error' }),
  });
}

function UserPanel({ id, cat }: { id: string; cat: Catalogue }) {
  const me = useMe();
  const lookups = useLookups();
  const q = useQuery({ queryKey: ['access-user', id], queryFn: () => get<UserDetail>(`/api/access/users/${id}`) });
  const { ask, node } = useConfirm();
  const [addRole, setAddRole] = useState(false);
  const [addPerm, setAddPerm] = useState(false);
  const [editOffices, setEditOffices] = useState(false);
  const u = q.data;
  const update = useAccessMutation((b: Record<string, unknown>) => patch(`/api/access/users/${id}`, b), 'Saved');
  const revokeRole = useAccessMutation((urId: string) => api(`/api/access/users/${id}/roles/${urId}`, { method: 'DELETE' }), 'Role removed');
  const revokePerm = useAccessMutation((pid: string) => api(`/api/access/users/${id}/permissions/${pid}`, { method: 'DELETE' }), 'Permission removed');
  const unlock = useAccessMutation(() => post(`/api/access/users/${id}/unlock`), 'Unlocked');
  const signOut = useAccessMutation(() => post(`/api/access/users/${id}/sign-out`), 'Signed out everywhere');
  const reset = useAccessMutation(() => post(`/api/access/users/${id}/reset-password`), 'Reset link sent');
  const self = me.data?.userId === id;
  const byApp = useMemo(() => {
    const m = new Map<string, UserDetail['roles']>();
    for (const r of u?.roles ?? []) m.set(r.app, [...(m.get(r.app) ?? []), r]);
    return [...m];
  }, [u?.roles]);
  const officeName = (oid: string | null) => (oid ? (lookups.data?.offices.find((o) => o.id === oid)?.name ?? 'office') : null);

  if (!u) return <Skeleton h={420} />;
  return (
    <div className="stack">
      <div className="panel" style={{ padding: 26, display: 'grid', gap: 16 }}>
        <div className="row" style={{ gap: 16, alignItems: 'flex-start' }}>
          <Avatar name={u.name} size={56} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="display" style={{ fontSize: '2rem', lineHeight: 1.05 }}>{u.name}</div>
            <div className="muted mono" style={{ fontSize: 12, marginTop: 4 }}>{u.email}{u.employeeNo ? ` · ${u.employeeNo}` : ''}</div>
            <div className="row" style={{ gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
              <Chip tone={u.status === 'active' ? 'ok' : 'bad'} live={u.activeSessions > 0}>{u.status === 'active' ? (u.activeSessions ? `Signed in · ${u.activeSessions}` : 'Active') : 'Blocked'}</Chip>
              {u.superadmin ? <Chip tone="accent">Superadmin</Chip> : null}
              {u.locked ? <Chip tone="warn">Locked out</Chip> : null}
              {u.mfa ? <Chip tone="info">2-step on</Chip> : null}
              <span className="faint" style={{ fontSize: 'var(--step--1)' }}>{u.lastLoginAt ? `Last sign-in ${relative(u.lastLoginAt)}` : 'Never signed in'}</span>
            </div>
          </div>
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <Button size="sm" icon="key" loading={reset.isPending} disabled={u.status !== 'active'} onClick={async () => (await ask('Send a password link?', `A single-use link goes to ${u.email}. Nobody sees or sets the password for them.`, 'Send link')) && reset.mutate(undefined)}>Send password link</Button>
          {u.locked ? <Button size="sm" icon="lock" loading={unlock.isPending} onClick={() => unlock.mutate(undefined)}>Unlock</Button> : null}
          <Button size="sm" variant="ghost" icon="logout" loading={signOut.isPending} disabled={!u.activeSessions} onClick={() => signOut.mutate(undefined)}>Sign out everywhere</Button>
          <span style={{ flex: 1 }} />
          {self ? null : u.status === 'active' ? (
            <Button size="sm" variant="danger" onClick={async () => (await ask(`Block ${u.name}?`, 'They are signed out of Hearth and every app that signs in through it, immediately.', 'Block', 'danger')) && update.mutate({ status: 'blocked' })}>Block</Button>
          ) : (
            <Button size="sm" onClick={() => update.mutate({ status: 'active' })}>Unblock</Button>
          )}
        </div>
        <div className="row" style={{ gap: 24, flexWrap: 'wrap' }}>
          {me.data?.isSuperadmin && !self ? <Switch checked={u.superadmin} onChange={async (v) => (!v || (await ask('Make superadmin?', 'Superadmins bypass every permission check in every app. Keep this list short.', 'Make superadmin', 'danger'))) && update.mutate({ superadmin: v })} label="Superadmin" showLabel /> : null}
        </div>
      </div>

      <Panel eyebrow={`Roles · ${u.roles.length}`} title="By app" action={<Button size="sm" icon="plus" onClick={() => setAddRole(true)}>Role</Button>}>
        {byApp.length ? (
          <div className="stack" style={{ ['--gap' as string]: '14px' }}>
            {byApp.map(([app, rs]) => (
              <div key={app} className="access-app">
                <div className="eyebrow">{cat.apps.find((a) => a.code === app)?.name ?? app}</div>
                <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                  {rs.map((r) => (
                    <span key={r.id} className="grant">
                      <span>{r.name}</span>
                      {r.officeId ? <span className="faint">· {officeName(r.officeId)}</span> : null}
                      {r.viaJobPosition ? <span className="faint" title="Granted by the job position template">· position</span> : (
                        <button type="button" className="grant__x" aria-label={`Remove ${r.name}`} onClick={() => revokeRole.mutate(r.id)}>
                          <Icon name="x" width={12} height={12} />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="muted">No roles. Without roles or permissions they can sign in but see only their own records.</p>
        )}
      </Panel>

      <div className="grid-12" style={{ gap: 18 }}>
        <div className="col-7">
          <Panel eyebrow={`Direct permissions · ${u.permissions.length}`} title="Exceptions to roles" action={<Button size="sm" variant="ghost" icon="plus" onClick={() => setAddPerm(true)}>Permission</Button>}>
            {u.permissions.length ? (
              <ul className="list-plain" style={{ maxHeight: 260, overflow: 'auto' }}>
                {u.permissions.map((p) => (
                  <li key={p.id} className="mini-row row row--between">
                    <span className="mono" style={{ fontSize: 12 }}>{p.key}</span>
                    <Button size="sm" variant="ghost" iconOnly icon="x" aria-label={`Remove ${p.key}`} onClick={() => revokePerm.mutate(p.id)} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">None. Prefer roles; direct grants are hard to review.</p>
            )}
          </Panel>
        </div>
        <div className="col-5">
          <Panel eyebrow="Offices" title={u.offices.length ? `${u.offices.length} assigned` : 'Unrestricted'} action={<Button size="sm" variant="ghost" onClick={() => setEditOffices(true)}>Edit</Button>}>
            {u.offices.length ? (
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>{u.offices.map((o) => <Chip key={o.id}>{o.name}</Chip>)}</div>
            ) : (
              <p className="muted" style={{ fontSize: 'var(--step--1)' }}>No office restriction. HR roles see every office; others see only their own records.</p>
            )}
          </Panel>
        </div>
      </div>
      {addRole ? <AddRoleDialog userId={id} cat={cat} held={u.roles} onClose={() => setAddRole(false)} /> : null}
      {addPerm ? <AddPermissionDialog userId={id} cat={cat} held={u.permissions.map((p) => p.id)} onClose={() => setAddPerm(false)} /> : null}
      {editOffices ? <OfficesDialog userId={id} current={u.offices.map((o) => o.id)} onClose={() => setEditOffices(false)} /> : null}
      {node}
    </div>
  );
}

function AddRoleDialog({ userId, cat, held, onClose }: { userId: string; cat: Catalogue; held: UserDetail['roles']; onClose: () => void }) {
  const lookups = useLookups();
  const [app, setApp] = useState(cat.apps.find((a) => cat.roles.some((r) => r.appCode === a.code))?.code ?? cat.apps[0]?.code ?? '');
  const [roleId, setRoleId] = useState('');
  const [officeId, setOfficeId] = useState('');
  const options = cat.roles.filter((r) => r.appCode === app);
  const permKeys = (r: Catalogue['roles'][number]) => r.permissionIds.map((pid) => cat.permissions.find((p) => p.id === pid)?.key ?? '');
  const grantable = (r: Catalogue['roles'][number]) => permKeys(r).every((k) => holds(cat.canGrant, k));
  const chosen = options.find((r) => r.id === roleId) ?? options.find(grantable);
  const m = useAccessMutation(() => post(`/api/access/users/${userId}/roles`, { roleId: chosen?.id, officeId: officeId || null }), 'Role granted');
  return (
    <Dialog open onClose={onClose} eyebrow="Grant" title="Add a role" width={560} footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} disabled={!chosen || !grantable(chosen)} onClick={() => m.mutate(undefined, { onSuccess: onClose })}>Grant</Button></>}>
      <div className="stack">
        <Field label="App">{(p) => <select {...p} className="select" value={app} onChange={(e) => { setApp(e.target.value); setRoleId(''); }}>{cat.apps.map((a) => <option key={a.code} value={a.code}>{a.name}</option>)}</select>}</Field>
        <Field label="Role" hint={chosen ? `${chosen.permissionIds.length} permissions · ${chosen.users} people` : undefined}>
          {(p) => (
            <select {...p} className="select" value={chosen?.id ?? ''} onChange={(e) => setRoleId(e.target.value)}>
              {options.map((r) => <option key={r.id} value={r.id} disabled={!grantable(r) || held.some((h) => h.roleId === r.id && !h.officeId)}>{r.name}{grantable(r) ? '' : ' — you don’t hold all its permissions'}</option>)}
            </select>
          )}
        </Field>
        <Field label="Only in office" hint="Optional: the role applies to one office">{(p) => <select {...p} className="select" value={officeId} onChange={(e) => setOfficeId(e.target.value)}><option value="">Every office they can see</option>{lookups.data?.offices.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select>}</Field>
        {chosen ? <div className="faint mono" style={{ fontSize: 11, lineHeight: 1.7 }}>{permKeys(chosen).slice(0, 14).join(' · ')}{chosen.permissionIds.length > 14 ? ' …' : ''}</div> : null}
        {m.error ? <Callout tone="bad">{err(m.error)}</Callout> : null}
      </div>
    </Dialog>
  );
}

function AddPermissionDialog({ userId, cat, held, onClose }: { userId: string; cat: Catalogue; held: string[]; onClose: () => void }) {
  const [q, setQ] = useState('');
  const m = useAccessMutation((pid: string) => post(`/api/access/users/${userId}/permissions`, { permissionId: pid }), 'Permission granted');
  const list = cat.permissions.filter((p) => !held.includes(p.id) && (q.length < 2 || p.key.includes(q.toLowerCase()) || p.name.toLowerCase().includes(q.toLowerCase()))).slice(0, 60);
  return (
    <Dialog open onClose={onClose} eyebrow="Grant" title="Add a direct permission" width={600} footer={<Button variant="ghost" onClick={onClose}>Done</Button>}>
      <div className="stack">
        <input className="input input--mono" autoFocus placeholder="Filter, e.g. hr.salary" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Filter permissions" />
        <ul className="list-plain" style={{ maxHeight: 360, overflow: 'auto' }}>
          {list.map((p) => {
            const ok = holds(cat.canGrant, p.key);
            return (
              <li key={p.id} className="mini-row row row--between" style={{ opacity: ok ? 1 : 0.45 }}>
                <span>
                  <span className="mono" style={{ fontSize: 12 }}>{p.key}</span>
                  {p.name !== p.key ? <span className="faint" style={{ fontSize: 'var(--step--1)' }}> · {p.name}</span> : null}
                </span>
                <Button size="sm" variant="ghost" icon="plus" disabled={!ok} loading={m.isPending && m.variables === p.id} onClick={() => m.mutate(p.id)} aria-label={`Grant ${p.key}`} />
              </li>
            );
          })}
        </ul>
      </div>
    </Dialog>
  );
}

function OfficesDialog({ userId, current, onClose }: { userId: string; current: string[]; onClose: () => void }) {
  const lookups = useLookups();
  const [sel, setSel] = useState<string[]>(current);
  const m = useAccessMutation(() => put(`/api/access/users/${userId}/offices`, { officeIds: sel }), 'Offices saved');
  return (
    <Dialog open onClose={onClose} eyebrow="Offices" title="Which offices they see" footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} onClick={() => m.mutate(undefined, { onSuccess: onClose })}>Save</Button></>}>
      <div className="stack" style={{ ['--gap' as string]: '10px' }}>
        <p className="muted" style={{ fontSize: 'var(--step--1)' }}>Leave all unticked for no restriction. Other GX apps receive these as <span className="mono">companyOfficeIds</span>.</p>
        {lookups.data?.offices.map((o) => (
          <Switch key={o.id} checked={sel.includes(o.id)} onChange={(v) => setSel(v ? [...sel, o.id] : sel.filter((x) => x !== o.id))} label={o.name} showLabel />
        ))}
        {m.error ? <Callout tone="bad">{err(m.error)}</Callout> : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------- Roles

function Roles({ cat }: { cat?: Catalogue }) {
  const [app, setApp] = useState<string | null>(null);
  const [roleId, setRoleId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  if (!cat) return <Skeleton h={360} />;
  const apps = cat.apps.filter((a) => cat.roles.some((r) => r.appCode === a.code) || cat.permissions.some((p) => p.appCode === a.code));
  const activeApp = app ?? apps[0]?.code ?? null;
  const roles = cat.roles.filter((r) => r.appCode === activeApp);
  const role = roles.find((r) => r.id === roleId) ?? roles[0] ?? null;
  return (
    <Reveal className="grid-12">
      <Reveal.Item className="col-4 stack" style={{ alignContent: 'start', ['--gap' as string]: '10px' }}>
        <Field label="App">{(p) => <select {...p} className="select" value={activeApp ?? ''} onChange={(e) => { setApp(e.target.value); setRoleId(null); }}>{apps.map((a) => <option key={a.code} value={a.code}>{a.name}</option>)}</select>}</Field>
        <div className="row row--between">
          <div className="eyebrow">Roles · {roles.length}</div>
          <Button size="sm" variant="ghost" icon="plus" onClick={() => setCreating(true)}>Role</Button>
        </div>
        {roles.map((r, i) => (
          <motion.button key={r.id} type="button" className="org-node" aria-pressed={r.id === role?.id} onClick={() => setRoleId(r.id)} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, ease: EASE, delay: i * 0.03 }}>
            <span className="org-node__name">{r.name}</span>
            <span className="display num" style={{ fontSize: '1.3rem', color: r.id === role?.id ? 'var(--accent-text)' : 'var(--ink-2)' }}>{r.users}</span>
            <span className="eyebrow" style={{ gridColumn: '1 / -1' }}>{r.key} · {r.permissionIds.length} permissions</span>
          </motion.button>
        ))}
      </Reveal.Item>
      <Reveal.Item className="col-8">{role ? <RoleEditor key={role.id} role={role} cat={cat} /> : <Empty title="No roles in this app yet." />}</Reveal.Item>
      {creating && activeApp ? <CreateRoleDialog app={activeApp} onClose={() => setCreating(false)} /> : null}
    </Reveal>
  );
}

function RoleEditor({ role, cat }: { role: Catalogue['roles'][number]; cat: Catalogue }) {
  const [sel, setSel] = useState<string[]>(role.permissionIds);
  const [q, setQ] = useState('');
  const m = useAccessMutation(() => put(`/api/access/roles/${role.id}/permissions`, { permissionIds: sel }), 'Role saved');
  const groups = useMemo(() => {
    const ps = cat.permissions.filter((p) => q.length < 2 || p.key.includes(q.toLowerCase()));
    const g = new Map<string, typeof ps>();
    for (const p of ps) g.set(p.appCode, [...(g.get(p.appCode) ?? []), p]);
    return [...g];
  }, [cat.permissions, q]);
  const dirty = sel.length !== role.permissionIds.length || sel.some((x) => !role.permissionIds.includes(x));
  return (
    <Panel eyebrow={`${role.appCode}:${role.key}`} title={role.name} action={<Button size="sm" variant="primary" disabled={!dirty} loading={m.isPending} onClick={() => m.mutate(undefined)}>Save {dirty ? `· ${sel.length}` : ''}</Button>}>
      <div className="stack">
        {role.users ? <Callout tone="info">{role.users} {role.users === 1 ? 'person holds' : 'people hold'} this role. Changes apply to all of them at their next request.</Callout> : null}
        <input className="input input--mono" placeholder="Filter permissions" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Filter permissions" />
        <div style={{ maxHeight: 460, overflow: 'auto' }} className="stack">
          {groups.map(([app, ps]) => (
            <div key={app}>
              <div className="eyebrow" style={{ margin: '6px 0' }}>{app} · {ps.filter((p) => sel.includes(p.id)).length}/{ps.length}</div>
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                {ps.map((p) => {
                  const ok = holds(cat.canGrant, p.key);
                  return (
                    <button key={p.id} type="button" className="chip-toggle" aria-pressed={sel.includes(p.id)} disabled={!ok} title={ok ? p.name : 'You don’t hold this permission'} onClick={() => setSel(sel.includes(p.id) ? sel.filter((x) => x !== p.id) : [...sel, p.id])}>
                      {p.key}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        {m.error ? <Callout tone="bad">{err(m.error)}</Callout> : null}
      </div>
    </Panel>
  );
}

function CreateRoleDialog({ app, onClose }: { app: string; onClose: () => void }) {
  const [f, setF] = useState({ key: '', name: '', description: '' });
  const m = useAccessMutation(() => post('/api/access/roles', { appCode: app, key: f.key, name: f.name, description: f.description || null }), 'Role created');
  return (
    <Dialog open onClose={onClose} eyebrow={app} title="New role" footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} onClick={() => m.mutate(undefined, { onSuccess: onClose })}>Create</Button></>}>
      <div className="stack">
        <Field label="Name">{(p) => <input {...p} className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value, key: f.key || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') })} />}</Field>
        <Field label="Key" hint="Stable identifier other apps check, e.g. warehouse_staff">{(p) => <input {...p} className="input input--mono" value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })} />}</Field>
        <Field label="Description">{(p) => <input {...p} className="input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />}</Field>
        {m.error ? <Callout tone="bad">{err(m.error)}</Callout> : null}
      </div>
    </Dialog>
  );
}
